// ============================================================================
// ui/artwork-hold.js — the buyer's ORIGINAL file, held in memory until submit
// ============================================================================
// Spec: `Weight-Designer-Bot/LOGO-ARTWORK-RETENTION-SPEC.md`.
//
// ── THE PROBLEM THIS EXISTS FOR ───────────────────────────────────────────────
// `logo-step.js` reassigns ONE variable down the whole ingest chain: decode +
// canvas re-encode at q=0.92 → Real-ESRGAN upscale / rembg background removal →
// transparent-margin trim. By the time anything is serialized, `logo.dataUrl` is
// the END of that chain, and the file the customer actually chose has been
// overwritten in place. It exists nowhere: not in KV, not in localStorage, not on
// the lead. Every quote collected before this module shipped has only the
// processed art.
//
// That processed art is the right thing to SHOW (it is what the buyer approved on
// the plate) and the wrong thing to MANUFACTURE from — Real-ESRGAN invents detail,
// and for a vector or high-res upload the stored version is strictly worse than
// what we were given.
//
// ── WHY A MODULE-SCOPED HOLDER AND NOT A FIELD ON THE PROFILE ─────────────────
// Ryan's call (2026-08-07): the original is retained at SUBMIT, so a browsing
// session that never asks for a price leaves nothing behind. Putting it on the
// design profile would defeat that twice over — `toAutosave` writes the profile to
// localStorage every 30s, and a share link carries the same string, so the file
// would be persisted (and doubled into every share) for something needed once.
// `design-profile.js` already documents and rejects that exact trade for the
// pre-key image.
//   ⚠ Capture is still at UPLOAD. It has to be: by submit the bytes are gone. What
// waits for submit is the PERSISTING, which is the part Ryan's rule is about.
//
// ── PENDING vs HELD, AND WHY THE TWO-STAGE COMMIT ────────────────────────────
// `handleFile` can fail after it has already stripped the previous logo
// (`beginReplace` runs before the decode). A single slot written at the top of
// ingest would then leave the app with NO logo and a holder full of bytes for a
// file it could not read — and submit would ship an "original" with no matching
// enhanced art beside it.
//   So `hold()` parks the file as PENDING and `commit()` promotes it, called only
// once ingest has really produced a logo. A failed upload leaves the pending slot
// to be overwritten by the next attempt and never becomes the answer.
//
// ⚠ THE HIGHEST-SEVERITY BUG IN THIS FEATURE IS A STALE HOLDER — customer A's file
// attached to customer B's lead. It is invisible on screen (nothing renders from
// here) and it is pure state, so it is asserted in `tools/verify-artwork-hold.mjs`
// rather than left to be noticed.
// ============================================================================

/** @typedef {{blob: Blob, fileName: string, mimeType: string, bytes: number}} HeldArtwork */

/** Promoted by `commit()` — what submit will actually upload. */
let held = null;
/** Written by `hold()` — a candidate that has not yet produced a working logo. */
let pending = null;

/**
 * ⚠ THE FIRST FILE WINS WITHIN ONE INGEST, and that is the whole reason this flag
 * exists. A PDF upload RE-ENTERS `handleFile` with a rendered PNG
 * (`return handleFile(png, opts)`), so a naive capture at the top of ingest would
 * record the PNG we generated rather than the PDF the customer supplied — losing
 * the one file a production team would actually want. The re-entry passes
 * `fromRender`, which makes `hold()` a no-op and leaves the PDF standing.
 */
export function hold(file, { fromRender = false } = {}) {
  if (fromRender) return false;          // a raster WE made — never the original
  if (!file || typeof file.size !== 'number' || !file.size) return false;
  pending = {
    blob: file,
    // Kept as a Blob rather than a data URL: base64 inflates ~33% and forces a
    // decode, while a Blob is a handle to bytes the browser already has and can be
    // posted straight into a FormData.
    fileName: String(file.name || 'logo'),
    mimeType: String(file.type || 'application/octet-stream'),
    bytes: file.size,
  };
  return true;
}

/** Ingest produced a real logo — the pending file is now the answer. */
export function commit() {
  if (!pending) return false;
  held = pending;
  pending = null;
  return true;
}

/** The buyer removed their logo, or the design was reset. Forget both slots. */
export function clear() {
  held = null;
  pending = null;
}

/**
 * What submit should upload, or null.
 * ⚠ NULL IS A REAL AND EXPECTED ANSWER, not an error — a design RESUMED from
 * autosave or opened from a `?d=` share link has a logo but no held file, because
 * the bytes were never persisted. Callers must report that honestly ("not
 * retained") rather than showing a broken link or saying nothing; a rep who sees
 * nothing assumes we have the artwork. This is the accepted cost of retaining at
 * submit only, and it is the same transient-after-restore hazard that
 * `logo.renderUrl` has now caused four separate bugs with.
 * @returns {?HeldArtwork}
 */
export function retained() {
  return held;
}

/** Test seam — `tools/verify-artwork-hold.mjs` drives the lifecycle without a DOM. */
export function _state() {
  return { held, pending };
}
